import { useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  type JobListItem,
  type OfferFileCounts,
  type QuoteListItem,
  type QuoteState,
} from '@k-management/shared';
import { Link, useNavigate } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { FilesPanel, useFolderFiles } from '../../components/FilesPanel.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { jobKeys, jobPath, JobStatePill, namesOf } from '../jobs/jobs-ui.js';
import { quoteKeys, quoteLabel, QuoteStatePill } from '../quotes/quotes-ui.js';

/** Offers whose negotiation is over without a yes: listed, in grey (decision of 26/09/2026). */
const CLOSED: QuoteState[] = ['rifiutata', 'annullata', 'superata'];

/**
 * The quotes of a client as the user sees them: the API keeps the data scope
 * («solo i propri»), so the count never reveals the quotes of others.
 * Disabled when the section does not apply.
 */
export function useSubjectQuotes(subjectId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...quoteKeys.all, 'client', subjectId],
    queryFn: () => apiRequest<QuoteListItem[]>(`/quotes?clientId=${subjectId}`),
    enabled,
  });
}

/**
 * Documenti of a subject (decision of 26/09/2026): the central place of the
 * registry. Each area stands on its own, with its source and its permissions;
 * today only the quotes, as links to the Preventivatore, never copies. The
 * folder of the subject in SharePoint will be one more area.
 */
export function SubjectDocuments({
  subjectId,
  showFiles,
  showQuotes,
  showJobs,
}: {
  subjectId: string;
  showFiles: boolean;
  showQuotes: boolean;
  showJobs: boolean;
}) {
  return (
    <section className="stack">
      {showFiles && <FilesArea subjectId={subjectId} />}
      {showJobs && <JobsArea subjectId={subjectId} />}
      {showQuotes && <QuotesArea subjectId={subjectId} />}
    </section>
  );
}

/** The files of the subject on SharePoint, through the API (docs/adr/ADR-009). */
export function useSubjectFiles(subjectId: string, enabled: boolean) {
  return useFolderFiles(`/subjects/${subjectId}/files`, ['subjects', subjectId, 'files'], enabled);
}

/** The folder of the subject in the site K-Apps, one per subject whatever its roles (ADR-010). */
function FilesArea({ subjectId }: { subjectId: string }) {
  const canWrite = useCan('anagrafica.write');
  return (
    <FilesPanel
      base={`/subjects/${subjectId}/files`}
      queryKey={['subjects', subjectId, 'files']}
      canWrite={canWrite}
      createFolder
      pathPrefix="Soggetti"
      noFolder="Il soggetto non ha ancora la sua cartella in SharePoint: si crea da sola con il primo file, una sola anche se il soggetto è cliente e fornitore."
      removeNote="L’eliminazione resta nello storico del soggetto."
    />
  );
}

function QuotesArea({ subjectId }: { subjectId: string }) {
  const navigate = useNavigate();
  const canCreate = useCan('preventivatore.write');
  const quotes = useSubjectQuotes(subjectId, true);
  const rows = quotes.data ?? [];
  // The files of each offer, counted for the offers the user sees (05/10/2026).
  const canDocuments = useCan('preventivatore.documents');
  const fileCounts = useQuery({
    queryKey: [...quoteKeys.all, 'files-by-client', subjectId],
    queryFn: () => apiRequest<OfferFileCounts>(`/quotes/files/by-client/${subjectId}`),
    enabled: canDocuments,
  });

  return (
    <section className="card stack">
      <div className="row between">
        <h2>
          Preventivi <span className="count-pill">{rows.length}</span>
        </h2>
        {canCreate && (
          <button
            type="button"
            className="primary"
            onClick={() => navigate(`/preventivatore/nuovo?cliente=${subjectId}`)}
          >
            Nuovo preventivo
          </button>
        )}
      </div>
      {quotes.isPending && <p className="muted">Caricamento…</p>}
      {quotes.isError && <p className="error">{quotes.error.message}</p>}
      {quotes.data && rows.length === 0 && (
        <p className="muted">Nessun preventivo per questo cliente.</p>
      )}
      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Preventivo</th>
                <th>Data</th>
                <th>Stato</th>
                <th className="right">Imponibile</th>
                <th>Di</th>
                {canDocuments && <th className="right">File</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((quote) => (
                <tr
                  key={quote.id}
                  className={CLOSED.includes(quote.state) || quote.expired ? 'closed' : undefined}
                >
                  <td>
                    <Link to={`/preventivatore/${quote.id}`}>{quoteLabel(quote)}</Link>
                  </td>
                  <td>{formatDate(quote.date)}</td>
                  <td>
                    <QuoteStatePill state={quote.state} expired={quote.expired} />
                  </td>
                  <td className="right">{formatMoney(quote.net)}</td>
                  <td>{quote.owner?.name ?? '—'}</td>
                  {canDocuments && (
                    <td className="right">
                      {quote.number ? (fileCounts.data?.[quote.number] ?? 0) : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small">
        Dal più recente. Un clic apre il preventivo nel Preventivatore: qui si guardano, non si
        copiano.
      </p>
    </section>
  );
}

/** The commesse of a client the user sees (ADR-022): the API keeps the data scope. */
export function useSubjectJobs(subjectId: string, enabled: boolean) {
  return useQuery({
    queryKey: jobKeys.client(subjectId),
    queryFn: () => apiRequest<JobListItem[]>(`/jobs?kind=commessa&clientId=${subjectId}`),
    enabled,
  });
}

function JobsArea({ subjectId }: { subjectId: string }) {
  const jobs = useSubjectJobs(subjectId, true);
  const rows = jobs.data ?? [];
  return (
    <section className="card stack">
      <h2>
        Commesse <span className="count-pill">{rows.length}</span>
      </h2>
      {jobs.isPending && <p className="muted">Caricamento…</p>}
      {jobs.isError && <p className="error">{jobs.error.message}</p>}
      {jobs.data && rows.length === 0 && (
        <p className="muted">Nessuna commessa per questo cliente.</p>
      )}
      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Commessa</th>
                <th>Stato</th>
                <th>PM</th>
                <th>Account</th>
                <th className="right">Offerte</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((job) => (
                <tr key={job.id}>
                  <td>
                    <Link to={jobPath(job)}>
                      {job.code} · {job.name}
                    </Link>
                  </td>
                  <td>
                    <JobStatePill state={job.state} />
                  </td>
                  <td>{namesOf(job.people, 'pm')}</td>
                  <td>{namesOf(job.people, 'account')}</td>
                  <td className="right">{job.offerCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
